/**
 * Temple remittances — `/v1/admin/temple-remittances` (doc 26 §5.8, doc 27 §5.7).
 *
 * **There is no amount in the record call, on purpose.** The server reads what a temple is owed
 * for the month out of `2002 temple_payable` and remits exactly that; a typed figure would be a
 * second answer to the same question. The console shows the server's figure and sends the UTR.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { idempotent } from '../idempotency';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type TempleRemittanceMonth = components['schemas']['TempleRemittanceMonth'];
export type TempleOwedRow = components['schemas']['TempleOwedRow'];
export type TempleRemittanceRow = components['schemas']['TempleRemittanceRow'];

/** `period` is any `YYYY-MM-DD` in the IST month; `null` holds the read until it is one. */
export function useTempleRemittanceMonth(period: string | null) {
  return useQuery({
    queryKey: keys.templeRemittances.month(period ?? ''),
    queryFn: () =>
      authedClient.request<TempleRemittanceMonth>(
        `/v1/admin/temple-remittances?period_month=${period}`,
      ),
    enabled: period !== null,
  });
}

export function useRecordTempleRemittance() {
  const client = useQueryClient();
  return useMutation<
    TempleRemittanceRow,
    unknown,
    {
      templeId: number;
      periodMonth: string;
      utr: string;
      processedOn: string;
      reason: string;
      key: string;
    }
  >({
    mutationFn: ({ templeId, periodMonth, utr, processedOn, reason, key }) =>
      authedClient.request<TempleRemittanceRow>('/v1/admin/temple-remittances', {
        method: 'POST',
        body: {
          temple_id: templeId,
          period_month: periodMonth,
          utr,
          processed_on: processedOn,
          reason,
        },
        headers: idempotent(key),
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.templeRemittances.all() });
    },
  });
}
